import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { searchPlaces, getCurrentLocation, getNearbyPlaces } from "../../functions/location";
import "./style.css";

export default function LocationPickerModal({ onClose, onSelectLocation, selectedLocation = null }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState(selectedLocation);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const { user } = useSelector((state) => ({ ...state })) || { user: null };
  const [recentLocations] = useState([
    {
      id: 1,
      name: "Home",
      address: "123 Oak Avenue, Downtown, NY",
      lat: 40.7128,
      lng: -74.0060,
      type: "home",
    },
    {
      id: 2,
      name: "Work Office",
      address: "456 Business Center, Midtown, NY",
      lat: 40.7589,
      lng: -73.9851,
      type: "work",
    },
    {
      id: 3,
      name: "Fitness Center",
      address: "789 Health Street, Uptown, NY",
      lat: 40.7505,
      lng: -73.9934,
      type: "gym",
    },
    {
      id: 4,
      name: "Local Coffee Shop",
      address: "321 Brew Avenue, Downtown, NY",
      lat: 40.7484,
      lng: -73.9857,
      type: "cafe",
    },
    {
      id: 5,
      name: "City Park",
      address: "654 Park Boulevard, Central, NY",
      lat: 40.7527,
      lng: -73.9772,
      type: "park",
    },
  ]);

  const searchPlacesAPI = useCallback(
    async (query) => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }

      setLoading(true);

      try {
        if (!user || !user.token) {
          console.error("User token not available");
          setSearchResults([]);
          return;
        }

        const response = await searchPlaces(query, user.token);
        if (response && response.status === "ok" && Array.isArray(response.data)) {
          setSearchResults(response.data);
        } else {
          console.error("Error searching places:", response?.message || "Unknown error");
          setSearchResults([]);
        }
      } catch (error) {
        console.error("Error searching places:", error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const getCurrentLocationData = useCallback(async () => {
    try {
      setLoading(true);
      const location = await getCurrentLocation();
      setCurrentLocation(location);

      if (location && location.lat && location.lng && user && user.token) {
        try {
          const nearbyResponse = await getNearbyPlaces(
            location.lat,
            location.lng,
            5000,
            user.token
          );
          if (
            nearbyResponse &&
            nearbyResponse.status === "ok" &&
            Array.isArray(nearbyResponse.data)
          ) {
            setNearbyPlaces(nearbyResponse.data);
          } else {
            console.error(
              "Error getting nearby places:",
              nearbyResponse?.message || "Unknown error"
            );
            setNearbyPlaces([]);
          }
        } catch (error) {
          console.error("Error getting nearby places:", error);
          setNearbyPlaces([]);
        }
      }
    } catch (error) {
      console.error("Error getting current location:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.token) {
      getCurrentLocationData();
    }
  }, [user, getCurrentLocationData]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() && user && user.token) {
        searchPlacesAPI(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, user, searchPlacesAPI]);

  const handleLocationSelect = (location) => {
    setSelected(location);
  };

  const handleDone = () => {
    onSelectLocation(selected);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleRemoveLocation = () => {
    setSelected(null);
    onSelectLocation(null);
    onClose();
  };

  const getLocationIcon = (type) => {
    switch (type) {
      case "restaurant":
        return "🍽️";
      case "cafe":
        return "☕";
      case "park":
        return "🌳";
      case "shopping":
        return "🛍️";
      case "library":
        return "📚";
      case "current":
        return "📍";
      case "home":
        return "🏠";
      case "work":
        return "🏢";
      case "gym":
        return "💪";
      case "recent":
        return "🏢";
      default:
        return "🏢";
    }
  };

  const renderLocationRow = (location, { showDistance = false } = {}) => {
    const isSelected = selected?.id === location.id;

    return (
      <button
        type="button"
        key={location.id}
        className={`loc_picker_row${isSelected ? " selected" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          handleLocationSelect(location);
        }}
        aria-pressed={isSelected}
      >
        <span className="loc_picker_icon">{getLocationIcon(location.type)}</span>
        <span className="loc_picker_meta">
          <span className="loc_picker_name">{location.name}</span>
          <span className="loc_picker_address">{location.address}</span>
          {showDistance && location.distance != null ? (
            <span className="loc_picker_distance">{location.distance}m away</span>
          ) : null}
        </span>
        <span className={`loc_picker_check${isSelected ? " checked" : ""}`}>
          {isSelected ? <i className="check_icon"></i> : null}
        </span>
      </button>
    );
  };

  const modal = (
    <div
      className="location_picker_modal"
      role="dialog"
      aria-modal="true"
      aria-label="Add Location"
      onClick={handleCancel}
    >
      <div
        className="location_picker_modal_content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="location_picker_modal_header">
          <h3>Add Location</h3>
          <button
            type="button"
            className="close_btn"
            onClick={handleCancel}
            aria-label="Close location picker"
          >
            <i className="exit_icon"></i>
          </button>
        </div>

        <div className="location_picker_search">
          <div className="search_wrap">
            <i className="maps_icon"></i>
            <input
              type="text"
              placeholder="Search for a place..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {loading && (
              <div className="search_loading">
                <div className="spinner">
                  <div className="bounce1"></div>
                  <div className="bounce2"></div>
                  <div className="bounce3"></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="location_picker_content">
          {selected && (
            <div className="loc_picker_selected">
              <div className="loc_picker_selected_header">
                <span>Selected Location</span>
                <button
                  type="button"
                  className="remove_btn"
                  onClick={handleRemoveLocation}
                >
                  Remove
                </button>
              </div>
              <div className="loc_picker_row selected static">
                <span className="loc_picker_icon">
                  {getLocationIcon(selected.type)}
                </span>
                <span className="loc_picker_meta">
                  <span className="loc_picker_name">{selected.name}</span>
                  <span className="loc_picker_address">{selected.address}</span>
                </span>
              </div>
            </div>
          )}

          {currentLocation && (
            <div className="loc_picker_section">
              <h4>Current Location</h4>
              <div className="loc_picker_list">
                {renderLocationRow(currentLocation)}
              </div>
            </div>
          )}

          {searchResults && searchResults.length > 0 && (
            <div className="loc_picker_section">
              <h4>Search Results</h4>
              <div className="loc_picker_list">
                {searchResults.map((location) => renderLocationRow(location))}
              </div>
            </div>
          )}

          {nearbyPlaces && nearbyPlaces.length > 0 && (
            <div className="loc_picker_section">
              <h4>Nearby Places</h4>
              <div className="loc_picker_list">
                {nearbyPlaces.map((location) =>
                  renderLocationRow(location, { showDistance: true })
                )}
              </div>
            </div>
          )}

          <div className="loc_picker_section">
            <h4>Recent Locations</h4>
            <div className="loc_picker_list">
              {recentLocations.map((location) => renderLocationRow(location))}
            </div>
          </div>

          {searchQuery &&
            searchResults &&
            searchResults.length === 0 &&
            !loading && (
              <div className="no_results">
                <span>No places found for "{searchQuery}"</span>
                <p>Try searching for a different place or check your spelling.</p>
              </div>
            )}
        </div>

        <div className="location_picker_modal_footer">
          <button type="button" className="cancel_btn" onClick={handleCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="done_btn"
            onClick={handleDone}
            disabled={!selected}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
